import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { onLCP, onCLS, onINP, onFCP, onTTFB } from 'web-vitals';

// Build marker — exposed on window so we can see in DevTools which bundle
// users actually loaded, plus forces webpack to keep it in the output (any
// non-tree-shakeable side effect creates a new content hash on every build).
// Bumped when we need to force a fresh deploy + cache invalidation.
if (typeof window !== 'undefined') {
  window.VROUM_BUILD = '2026-05-13-rating-quarantine-deploy';
  // Console marker so users / devs / owner can verify which bundle their
  // browser actually loaded (the cache-stuck-on-babca5df problem).
  // Look for "VROUM build:" in DevTools console — should be fresh date.
  // eslint-disable-next-line no-console
  console.log('VROUM build:', window.VROUM_BUILD);
}

// Report Core Web Vitals to GA4 + backend mirror.
// LCP, CLS, INP are direct Google ranking signals (mobile especially).
const reportVital = ({ name, value, rating, id }) => {
  const rounded = Math.round(name === 'CLS' ? value * 1000 : value);
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', name, {
      value: rounded,
      metric_id: id,
      metric_rating: rating, // 'good' | 'needs-improvement' | 'poor'
      transport_type: 'beacon',
    });
  }
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/track/event', new Blob(
        [JSON.stringify({ name, params: { value: rounded, rating, path: window.location.pathname } })],
        { type: 'application/json' }
      ));
    }
  } catch (_) {}
};

onLCP(reportVital);
onCLS(reportVital);
onINP(reportVital);
onFCP(reportVital);
onTTFB(reportVital);

const rootElement = document.getElementById("root");

// The SEO lambda (api/[...path].js, handleSeoMetaHtml) writes crawlable body
// copy into #root so Googlebot and AI crawlers get real text instead of an
// 18-word shell. That markup is NOT a React render, so hydrateRoot() would
// mismatch against it. Drop it first and mount clean — createRoot() replaces
// the container's children, so users never see it.
const seoPrerender = rootElement.querySelector("#seo-prerender");
if (seoPrerender) {
  // CLS fix (Lighthouse: /synergeia CLS 1.7-1.9 vs 0 on the static Astro pages).
  // The prerender block is often 1500px+; removing it before React paints its own
  // content collapses #root to ~0 and slams the footer up the viewport = a giant
  // layout shift. Pin #root to the prerender's current height across the swap so
  // nothing below it moves, then release once the page has settled (so short React
  // pages don't keep dead whitespace). Only affects swap timing, not what renders.
  const pinned = rootElement.offsetHeight;
  if (pinned > 0) {
    // Pin #root to the prerender's height, permanently. `min-height` only sets a
    // FLOOR: React content taller than the prerender still grows freely, but the
    // container can never COLLAPSE below it — which is what slammed the layout up
    // the viewport (Lighthouse CLS 1.9). Earlier attempts released this reserve
    // after load, but the release itself raced React's async content and re-created
    // an intermittent ~0.9 shift. Never releasing removes the race: worst case is a
    // little empty space at the very bottom of a page that has no footer element
    // below #root, which is invisible. CLS → 0, deterministically.
    rootElement.style.minHeight = pinned + "px";
  }
  seoPrerender.remove();
}

// Check if the app was pre-rendered by react-snap
// If so, hydrate instead of render for better performance
if (rootElement.hasChildNodes()) {
  // Pre-rendered content exists - hydrate
  ReactDOM.hydrateRoot(
    rootElement,
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} else {
  // No pre-rendered content - normal render (development)
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}
// build cache bust 2026-05-18T09:41:52Z
